import {
  AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import dayjs from 'dayjs';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  DateRangeSelectorPredefinedRangesComponent, IDateRangeOption,
} from 'src/components/shared/date-range-selector/date-range-selector-predefined-ranges/date-range-selector-predefined-ranges.component';
import { DateRangeSelectorProvider } from 'src/providers/date-range-selector.provider';
import { TranslateService } from '../../../app/utils/translate.service';

interface DateSelection {
  start: Date;
  end: Date;
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'D MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'D MMM YYYY',
    monthYearA11yLabel: 'MMM YYYY',
  },
};

@Component({
    selector: 'app-date-range-selector',
    templateUrl: './date-range-selector.component.html',
    styleUrls: ['./date-range-selector.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DateRangeSelectorComponent implements AfterViewInit, OnDestroy {
  @ViewChild(DateRangeSelectorPredefinedRangesComponent) preDefinedRangesComponent: DateRangeSelectorPredefinedRangesComponent;

  @Input() selection: DateSelection;

  @Output() selectionChange = new EventEmitter();

  @Input() timeScale = 'month';

  @Input() maxDate?: Date;

  @Output() timeScaleChange = new EventEmitter();

  /**
   * If this property is set to true, the value of this component will not be dependent on the global value
   * retrieved from the DateRangeProvider, instead this component will track it's own state
   * Defaults to: false
   */
  @Input() local = false;

  public stop$ = new Subject();

  public displayValue$ = new BehaviorSubject('');

  public selectedRange$ = new BehaviorSubject<IDateRangeOption>(null);

  DEBOUNCE_TIME = 500;

  public selection$ = new BehaviorSubject(null);

  public timescale$ = new BehaviorSubject(null);

  selectedDates;

  expanded = false;

  selectedType = 'month';

  dateGroup: FormGroup<any>;

  constructor(
    private $dateRange: DateRangeSelectorProvider,
    private changeDetectorRef: ChangeDetectorRef,
    private $translateService: TranslateService,
  ) {
    this.dateGroup = new UntypedFormGroup({
      start: new UntypedFormControl(),
      end: new UntypedFormControl(),
    });

    this.dateGroup.valueChanges
      .pipe(
        takeUntil(this.stop$),
        filter((v) => v.start && v.end),
        distinctUntilChanged(
          (x, y) => dayjs(x.start).isSame(dayjs(y.start)) && dayjs(x.end).isSame(dayjs(y.end)),
        ),
        tap((dates) => { this.selectedDates = dates; }),
        tap((dates) => this.updateDisplayValue(dates)),
        debounceTime(this.DEBOUNCE_TIME),
      )
      .subscribe((values) => {
        this.updateSelection(values);
      });
  }

  private updateDisplayValue(dates: { start: Date, end: Date }): void {
    const dayjsDates = { start: dayjs(dates.start).startOf('day'), end: dayjs(dates.end).endOf('day') };

    const matchingPredefinedRange = this.preDefinedRangesComponent.getMatchingPredefinedRange(dayjsDates.start, dayjsDates.end);

    if (matchingPredefinedRange) {
      this.displayValue$.next(this.$translateService.instant(matchingPredefinedRange.name));
    } else {
      const localizedStart = dayjsDates.start.locale(this.$translateService.currentLang).format('D MMM YYYY');
      const localizedEnd = dayjsDates.end.locale(this.$translateService.currentLang).format('D MMM YYYY');
      this.displayValue$.next(`${localizedStart} - ${localizedEnd}`);
    }
  }

  updateSelection(dates) {
    this.selection = dates;
    this.selectionChange.emit(dates);
    this.selection$.next(dates);

    if (!this.local) {
      this.$dateRange.selection$.next(dates);
    }
  }

  updateTimeScale(scale: string) {
    if (this.local) {
      this.timescale$.next(scale);
    } else {
      this.$dateRange.timeScale$.next(scale);
    }

    this.timeScale = scale;
    this.timeScaleChange.emit(scale);
  }

  async ngAfterViewInit() {
    const currentSelection = this.selection ?? this.$dateRange.selection$.value;
    const { start, end } = currentSelection;

    this.setDates(start, end);
    this.updateSelection({ start, end });

    this.$translateService
      .onInitialLangAndLangChange$
      .pipe(takeUntil(this.stop$))
      .subscribe(() => this.updateDisplayValue(this.selectedDates));
  }

  selectPredefinedRange(range: IDateRangeOption) {
    this.setDates(range.start, range.end);
  }

  slideRange(dir: string) {
    const value = this.dateGroup.value.start;
    const date = dayjs(value);
    switch (this.selectedType) {
      case 'day': {
        let newDate;
        if (dir === 'right') {
          newDate = date.add(1, 'days').toDate();
        } else {
          newDate = date.subtract(1, 'days').toDate();
        }
        this.setDates(newDate, newDate);
        break;
      }
      case 'year':
      case 'month':
      case 'week': {
        const type = `${this.selectedType}s` as any;
        if (dir === 'right') {
          date.add(1, type);
        } else {
          date.subtract(1, type);
        }
        const newStart = date.startOf(this.selectedType).toDate();
        const newEnd = date.endOf(this.selectedType).toDate();
        this.setDates(newStart, newEnd);
        break;
      }
      default:
    }
  }

  setDates(start: Date, end: Date, doNotEmit = false): void {
    const dates = { start: dayjs(start).toDate(), end: dayjs(end).toDate() };

    if (doNotEmit) {
      this.dateGroup.setValue(dates, { onlySelf: true, emitEvent: false });
    } else {
      this.dateGroup.setValue(dates);
    }

    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.stop$.next(undefined);
  }
}
